import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Input, Modal, Row, Space, Tag, Popover, Typography, Dropdown } from "antd";
import moment from "moment";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PlusOutlined,
  MoreOutlined,

} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";

import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { allPermissions } from "../../shared/constants/constants";
import { RootState } from "../../shared/types/common";
import {
  accessAllowed,
  showNotification,
} from "../../shared/utils/utils";

import {
  getEmailConfigurations,
  createEmailConfiguration,
  updateEmailConfiguration,
  deleteEmailConfiguration,
} from "../../core/store/reducers/email-configuration.slice";
import CreateEditEmailConfigurationComponent from "./components/create-edit-email-configuration";

const EMAIL_FILTER_LABELS: Record<string, string> = {
  search: "Search",
  status: "Status",
  scheduleType: "Trigger",
  scheduleTime: "Schedule Time",
  module: "Module"
};

function emailFilterValueText(key: string, value: any): string {
  switch (key) {
    case "status":
      return value === true ? "ACTIVE" : "INACTIVE";

    default:
      return String(value).trim();
  }
}

const EMAIL_FILTER_CLEAR: Record<string, any> = {
  search: undefined,
  status: undefined,
  scheduleType: undefined,
  scheduleTime: undefined,
  module: undefined,
};

const ManageEmailPage: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = useForm();

  /**
   * Redux state
   *
   * Change this according to your email configuration slice structure.
   */
  const { loading, list, pagination } = useSelector(
    (state: RootState) => state.email
  );

  console.log("Email pagination:", pagination);
  console.log("Total rows:", pagination?.total);

  const { myPermissions } = useSelector(
    (state: RootState) => state.auth
  );

  const { screenType } = useSelector(
    (state: RootState) => state.settings
  );

  const [filterVisible, setFilterVisible] = useState(false);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);

  const [deleteModalVisible, setDeleteModalVisible] =
    useState(false);

  const [emailIdToDelete, setEmailIdToDelete] =
    useState<string>("");

  const [addModalVisible, setAddModalVisible] =
    useState(false);

  const [email, setEmail] = useState("");

  const [emailListData, setEmailListData] =
    useState<any[]>([]);

  const [filters, setFilters] = useState({} as any);
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "DESC" });

  const [pageSize, setPageSize] =
    useState(TABLE_ROWS_COUNT);

  /**
   * Load email configuration list
   */
  useEffect(() => {
    loadList(1);    
  }, [filters, pageSize, sorter]);

  /**
   * Map API response to table data
   */
  useEffect(() => {
    setEmailListData(
      (list || []).map((obj: any, index: number) => ({
        key: obj.id || index,
        ...obj,
      }))
    );
  }, [list]);

  /**
   * Get Email Configuration List
   */
  const loadList = useCallback((page: any = null) => {
    const params = {
      page: page
        ? page
        : pagination?.page
          ? pagination.page
          : 1,
      limit: pageSize,
      sort_by: sorter.sort_by || "",
      sort_type: sorter.sort_type || "DESC",
    };

    const body = {} as any;

    Object.keys(filters || {}).forEach((key: string) => {
      if (isFilterValueActive(filters[key])) {
        body[key] = filters[key];
      }
    });

    dispatch(
      getEmailConfigurations({
        params,
        body,
      }) as any
    );
  }, [dispatch, filters, pageSize, pagination?.page, sorter]
  
  );

  /**
   * Open Add Email Modal
   */
  const createEmailListener = () => {

  };

  /**
   * Close Add Email Modal
   */
  const closeAddModal = () => {
    setEmail("");
    setAddModalVisible(false);
  };

  /**
   * Add Email
   */
  const handleAddEmail = () => {
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      showNotification(
        "Please enter an email ID",
        "error"
      );
      return;
    }

    /**
     * Email validation
     */
    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmedEmail)) {
      showNotification(
        "Please enter a valid email ID",
        "error"
      );
      return;
    }

    /**
     * Check duplicate email in current list
     */
    const alreadyExists = emailListData.some(
      (item: any) =>
        String(item.email || "")
          .toLowerCase() === trimmedEmail
    );

    if (alreadyExists) {
      showNotification(
        "Email ID already exists",
        "error"
      );
      return;
    }

    (
      dispatch(
        createEmailConfiguration({
          email: trimmedEmail,
        })
      ) as any
    ).then((res: any) => {
      if (
        res.meta?.requestStatus === "fulfilled"
      ) {
        showNotification(
          "Email ID added successfully",
          "success"
        );

        closeAddModal();

        loadList(1);
      }
    });
  };

  /**
   * Open Delete Modal
   */
  const handleDeleteModal = (id: string) => {
    setEmailIdToDelete(id);
    setDeleteModalVisible(true);
  };

  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };



  /**
   * Delete Email
   */
  const handleDelete = (id: string) => {
    (
      dispatch(deleteEmailConfiguration(id)) as any
    ).then((res: any) => {
      if (
        res.meta?.requestStatus === "fulfilled"
      ) {
        showNotification(
          "Email ID deleted successfully",
          "success"
        );

        setDeleteModalVisible(false);
        setEmailIdToDelete("");

        loadList();
      }
    });
  };

  /**
   * Confirm Delete
   */
  const onDeleteConfirm = () => {
    if (emailIdToDelete) {
      handleDelete(emailIdToDelete);
    }
  };

  /**
   * Cancel Delete
   */
  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setEmailIdToDelete("");
  };

  const getSortOrder = (columnKey: string) => {
    if (sorter.sort_by !== columnKey) return null;
    return sorter.sort_type === "ASC" ? "ascend" : "descend";
  };

  const getSortableColumnProps = (columnKey: string) => ({
    sorter: true,
    sortOrder: getSortOrder(columnKey),
  });

  const onChangeHandler = (_pagination: any, _filters: any, tableSorter: any) => {
    const activeSorter = Array.isArray(tableSorter)
      ? tableSorter.find((item) => item?.order)
      : tableSorter;

    if (!activeSorter?.order) {
      setSorter({ sort_by: "", sort_type: "DESC" });
      return;
    }

    setSorter({
      sort_by: activeSorter.columnKey || "",
      sort_type: activeSorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  /**
   * Pagination
   */
  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };

  /**
   * Page size
   */
  const onPageSizeChange = (
    _pageNumber: number,
    selectedPageSize: number
  ) => {
    setPageSize(selectedPageSize);
  };

  /**
   * Apply filters
   */
  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  /**
   * Clear filters
   */
  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  /**
   * Remove individual filter
   */
  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      form.setFieldsValue({
        [key]: EMAIL_FILTER_CLEAR[key],
      });
    },
    [form]
  );

  /**
   * Active filter chips
   */
  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((key) =>
        isFilterValueActive(filters[key])
      )
      .map((key) => ({
        key,
        fieldLabel:
          EMAIL_FILTER_LABELS[key] || key,
        valueText: emailFilterValueText(
          key,
          filters[key]
        ),
      }));
  }, [filters]);

  /**
   * Permission check
   *
   * Change these permissions if you have
   * separate email permissions.
   */
  const hasEmailAccess =
    accessAllowed(
      myPermissions,
      allPermissions.CREATE_EMAIL
    ) ||
    accessAllowed(
      myPermissions,
      allPermissions.DELETE_EMAIL
    );

  /**
   * Table Columns
   */
  const { Text } = Typography;

  const columns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 100,
      render: (_text: any, record: any) =>
        record.module === "Others"
          ? record.otherModule || "Others"
          : record.module,
      ...getSortableColumnProps("module"),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      width: 280,
      ...getSortableColumnProps("subject"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      ...getSortableColumnProps("status"),
      render: (status: boolean | number) => (
        <Tag color={status ? "green" : "default"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    // {
    //   title: "Subject",
    //   dataIndex: "subject",
    //   key: "subject",
    //   width: 220,
    //   ellipsis: true,
    // },

    {
      title: "Trigger",
      dataIndex: "scheduleType",
      key: "scheduleType",
      width: 130,
      ...getSortableColumnProps("scheduleType"),
      render: (scheduleType: string) => {
        if (scheduleType === "DAILY") {
          return "Every Day";
        }

        if (scheduleType === "WEEKLY") {
          return "Weekly";
        }

        if (scheduleType === "ON_TRIGGER") {
          return "On Trigger";
        }

        return "-";
      },
    },
    {
      title: "Schedule",
      key: "schedule",
      width: 180,
      ...getSortableColumnProps("schedule"),
      render: (_text: any, record: any) => {
        if (record?.scheduleType === "ON_TRIGGER") {
          return "-";
        }

        const day = record?.scheduleDay;
        const time = record?.scheduleTime;

        if (day && time) {
          return `${day} - ${time}`;
        }

        if (day) {
          return day;
        }

        if (time) {
          return time;
        }

        return "-";
      },
    },

    {
      title: "Recipients",
      dataIndex: "recipients",
      key: "recipients",
      width: 150,
      ...getSortableColumnProps("recipients"),
      render: (recipients: any[]) => {
        const activeRecipients =
          recipients?.filter((recipient) => recipient?.isActive) || [];

        if (!activeRecipients.length) {
          return "-";
        }

        const recipientContent = (
          <div style={{ minWidth: 280, maxWidth: 400 }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              Recipients ({activeRecipients.length})
            </div>

            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {activeRecipients.map((recipient, index) => (
                <div
                  key={recipient.id || recipient.emailId || index}
                  style={{
                    padding: "6px 0",
                    borderBottom:
                      index !== activeRecipients.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <MailOutlined style={{ marginRight: 8 }} />
                  <Text>{recipient.emailId}</Text>
                </div>
              ))}
            </div>
          </div>
        );

        return (
          <Popover
            content={recipientContent}
            title={null}
            trigger="click"
            placement="bottomLeft"
          >
            <Button type="link" style={{ padding: 0 }}>
              {activeRecipients.length}{" "}
              {activeRecipients.length === 1 ? "Recipient" : "Recipients"}
            </Button>
          </Popover>
        );
      },
    },
    // {
    //   title: "Created Date",
    //   dataIndex: "createdDate",
    //   key: "createdDate",
    //   width: 140,
    //   ...getSortableColumnProps("createdDate"),
    //   render: (createdDate: string) =>
    //     createdDate ? moment(createdDate).format("DD-MMM-YYYY") : "-",
    // },
    {
      title: "Updated Date",
      dataIndex: "updatedDate",
      key: "updatedDate",
      width: 140,
      ...getSortableColumnProps("updatedDate"),
      render: (updatedDate: string) =>
        updatedDate ? moment(updatedDate).format("DD-MMM-YYYY") : "-",
    },

    ...(hasEmailAccess
      ? [
        // {
        //   title: "Actions",
        //   key: "actions",
        //   fixed: "right" as const,
        //   width: 100,

        //   render: (_text: any, record: any) => (
        //     <Space>
        //       {accessAllowed(
        //         myPermissions,
        //         allPermissions.UPDATE_EMAIL
        //       ) && (
        //         <Button
        //           type="link"
        //           onClick={() => editEntityListner(record)}
        //           icon={<EditOutlined />}
        //         />
        //       )}

        //       {accessAllowed(
        //         myPermissions,
        //         allPermissions.DELETE_EMAIL
        //       ) && (
        //         <Button
        //           type="link"
        //           onClick={() =>
        //             handleDeleteModal(record?.id)
        //           }
        //           icon={
        //             <DeleteOutlined
        //               style={{ color: "#FF0000" }}
        //             />
        //           }
        //         />
        //       )}
        //     </Space>
        //   ),
        // },
        // {
        //   title: "Actions",
        //   key: "actions",
        //   fixed: "right" as const,
        //   width: 100,

        //   render: (_text: any, record: any) => {
        //     const menuItems = [
        //       ...(accessAllowed(
        //         myPermissions,
        //         allPermissions.UPDATE_EMAIL
        //       )
        //         ? [
        //           {
        //             key: "edit",
        //             label: "Edit",
        //             icon: <EditOutlined />,
        //           },
        //         ]
        //         : []),

        //       ...(accessAllowed(
        //         myPermissions,
        //         allPermissions.DELETE_EMAIL
        //       )
        //         ? [
        //           {
        //             key: "delete",
        //             label: "Delete",
        //             icon: <DeleteOutlined />,
        //             danger: true,
        //           },
        //         ]
        //         : []),
        //     ];

        //     return (
        //       <Dropdown
        //         menu={{
        //           items: menuItems,
        //           onClick: ({ key }) => {
        //             if (key === "edit") {
        //               editEntityListner(record);
        //             }

        //             if (key === "delete") {
        //               handleDeleteModal(record?.id);
        //             }
        //           },
        //         }}
        //         trigger={["click"]}
        //         placement="bottomRight"
        //       >
        //         <Button
        //           type="text"
        //           icon={<MoreOutlined />}
        //         />
        //       </Dropdown>
        //     );
        //   },
        // }
        {
          title: "Actions",
          key: "actions",
          fixed: "right" as const,
          width: 100,

          render: (_text: any, record: any) => (
            <Space size="middle">
              {accessAllowed(myPermissions, allPermissions.UPDATE_EMAIL) && (
                <EditOutlined
                  style={{ cursor: "pointer" }}
                  onClick={() => editEntityListner(record)}
                />
              )}

              {accessAllowed(myPermissions, allPermissions.DELETE_EMAIL) && (
                <DeleteOutlined
                  style={{ cursor: "pointer", color: "red" }}
                  onClick={() => handleDeleteModal(record?.id)}
                />
              )}
            </Space>
          ),
        }
      ]
      : []),
  ];
  /**
   * Filter Form
   */
  const [selectedModule, setSelectedModule] = useState(
    form.getFieldValue("module") || ""
  );
  const formProps = {
    form,

    name: "manage-email-filters",

    enableEnterSubmit: true,

    formElements: [
      {
        name: "search",
        label: "",
        placeholder: "Search configurations",
        type: "Input",

        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 4,
        },
      },

      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",

        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 4,
        },

        allowClear: true,

        options: [
          {
            value: true,
            label: "ACTIVE",
          },
          {
            value: false,
            label: "INACTIVE",
          },
        ],
      },
      {
        name: "module",
        label: "",
        placeholder: "module",
        type: "Select",

        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 4,
        },

        allowClear: true,

        options: [
          {
            value: "Staff",
            label: "Staff",
          },
          {
            value: "Project",
            label: "Project",
          },
          {
            value: "Milestone",
            label: "Milestone"
          },
          {
            value: "Others",
            label: "Others"
          }
        ],
        onChange: (value: string) => {
          setSelectedModule(value);
          if (value !== "Others") {
            form.setFieldValue("otherModule", undefined);
          }
        },
      },
      ...(selectedModule === "Others"
        ? [
          {
            name: "otherModule",
            label: "",
            placeholder: "Enter module name",
            type: "Input",
            colSpanResponsive: {
              xs: 12,
              sm: 8,
              md: 6,
              lg: 4,
            },
            allowClear: true,
            rules: [
              {
                required: false,
                message: "Please enter module name",
              },
            ],
          },
        ]
        : []),
      {
        name: "scheduleType",
        label: "",
        placeholder: "Trigger",
        type: "Select",
        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 4,
        },
        allowClear: true,
        options: [
          { value: "DAILY", label: "Every Day" },
          { value: "WEEKLY", label: "Weekly" },
          { value: "ON_TRIGGER", label: "On Trigger" },
        ],
      },

      // {
      //  name: "scheduleTime",
      //  label: "",
      // placeholder: "Schedule Time (e.g. 09:00)",
      //type: "Input",
      //colSpanResponsive: {
      //  xs: 12,
      //  sm: 8,
      // md: 6,
      //lg: 4,
      // },
      //},

    ],

    initialValues: {},

    extraButtons: [
      {
        htmlType: "submit",

        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 3,
        },

        loading: false,

        submitButtonText: "Clear",

        type: "clearForm",

        onClick: clearAllFilters,
      },

      {
        htmlType: "submit",

        colSpanResponsive: {
          xs: 12,
          sm: 8,
          md: 6,
          lg: 3,
        },

        loading: false,

        submitButtonText: "Apply",

        submitButtonType: "primary",
      },
    ],

    onFinish: onFilterApply,

    onFinishFailed: null,

    onValuesChange: () => {
      return;
    },
  };

  /**
   * Header Buttons
   */
  const headerButtons = (
    <Space
      size="middle"
      className="app-page-actions"
    >
      {accessAllowed(
        myPermissions,
        allPermissions.CREATE_EMAIL
      ) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={createEntityListner}
          >
            <span className="font-size-14">
              Add{" "}
              {!screenType?.isPortrait &&
                "Configuration"}
            </span>
          </Button>
        )}

      {/* <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() =>
          setFilterVisible(!filterVisible)
        }
        title={
          filterVisible
            ? "Hide filters"
            : "Show filters"
        }
      /> */}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible((visible) => !visible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
        aria-label={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      {/* Page Header */}
      <div className="app-page-header">
        <Row
          align="middle"
          justify="space-between"
          wrap
        >
          <Col
            flex="1"
            style={{ minWidth: 0 }}
          >
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <MailOutlined />
              </div>

              <h1 className="app-page-title">
                Email Configuration
              </h1>
            </div>

            <p className="app-page-subtitle">
              Manage notification email settings, recipients and schedules.
            </p>
          </Col>

          <Col>
            {headerButtons}
          </Col>
        </Row>
      </div>

      {/* Filters */}
      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt
                style={{ fontSize: 14 }}
              />

              Filters
            </div>

            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() =>
                setFilterVisible(false)
              }
              title="Close filters"
              aria-label="Close filters"
            />
          </div>

          <div className="table-filter-form">
            <GenericFormComponent
              {...formProps}
            />
          </div>
        </div>
      )}

      {/* Active Filters */}
      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      {/* Table */}
      <div className="app-table-card milestones-table-card">
        <DataTableComponent
          rowKey="id"
          pagination={true}
          loading={loading}
          dataSource={emailListData}
          columns={columns}
          defaultPage={0}
          currentPage={pagination?.page}
          totalRows={pagination?.total}
          onChange={onChangeHandler}
          onPageChange={onPageChange}
          onPageSizeChange={
            onPageSizeChange
          }
          pageSize={pageSize}
          pageSizeOptions={["10", "15", "25", "50", "100"]}
          tableWidth="max-content"
        />
      </div>

      {createEditOpen && (
        <CreateEditEmailConfigurationComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}

      {/* Add Email Modal */}
      <Modal
        title="Add Email"
        open={addModalVisible}
        onOk={handleAddEmail}
        onCancel={closeAddModal}
        okText="Add"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 8 }}>
          Email ID
        </div>

        <Input
          placeholder="Enter email ID"
          value={email}
          onChange={(event) =>
            setEmail(event.target.value)
          }
          onPressEnter={handleAddEmail}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          danger: true,
        }}
      >
        <p>
          Are you sure you want to delete this?
        </p>
      </Modal>
    </div>
  );
};

export default ManageEmailPage;
